import { useEffect, useRef } from "react";

/**
 * Custom hook to manage all Gantt chart event handlers and interceptors
 * Provides a clean separation of event handling logic from component rendering
 */
const useGanttEventHandlers = ({ ganttApi, onTaskSelect }) => {
	const lastClickTime = useRef(0);
	const lastClickedTaskId = useRef(null);

	// Fixed configuration for EMI Gantt
	const DOUBLE_CLICK_THRESHOLD_MS = 300;
	const MIN_CLICK_INTERVAL_MS = 100; // Minimum time between clicks to prevent duplicate events

	useEffect(() => {
		if (!ganttApi) return;

		const cleanupFunctions = [];

		// Task selection handler with double-click requirement
		const unsubscribeTaskSelect = ganttApi.on("select-task", (ev) => {
			if (!onTaskSelect) return;

			const now = Date.now();
			const timeSinceLastClick = now - lastClickTime.current;
			const isSameTask = lastClickedTaskId.current === ev.id;

			// Require double-click to open editor with minimum interval to prevent duplicate events
			if (
				isSameTask &&
				timeSinceLastClick >= MIN_CLICK_INTERVAL_MS &&
				timeSinceLastClick <= DOUBLE_CLICK_THRESHOLD_MS
			) {
				onTaskSelect(ev.id);
				// Reset to prevent triple-click issues
				lastClickTime.current = 0;
				lastClickedTaskId.current = null;
			} else if (timeSinceLastClick < MIN_CLICK_INTERVAL_MS && isSameTask) {
				// Ignore rapid duplicate events - don't update lastClickTime
			} else {
				lastClickTime.current = now;
				lastClickedTaskId.current = ev.id;
			}
		});

		// Always disable these features for EMI Gantt
		const unsubscribeEditorIntercept = ganttApi.intercept(
			"show-editor",
			() => false,
		);
		const unsubscribeSortIntercept = ganttApi.intercept(
			"sort-tasks",
			() => false,
		);
		const unsubscribeAddIntercept = ganttApi.intercept("add-task", () => false);
		const unsubscribeDeleteIntercept = ganttApi.intercept(
			"delete-task",
			() => false,
		);
		const unsubscribeAddLinkIntercept = ganttApi.intercept(
			"add-link",
			() => false,
		);
		const unsubscribeDeleteLinkIntercept = ganttApi.intercept(
			"delete-link",
			() => false,
		);

		// Only services in a work order that has not been started can be moved, and
		// only sideways. The library picks resize over move when the bar is grabbed
		// near an edge, and reports it as a change in width. Compared against the
		// task's live width rather than a snapshot: a move keeps the width equal on
		// every event, a resize differs from the first event (which this rejects,
		// so the stored width never drifts), and no state can go stale between
		// drags, commits or zooms.
		const unsubscribeDragIntercept = ganttApi.intercept("drag-task", (ev) => {
			const task = ganttApi.getTask(ev.id);
			if (!task?.isDraggable) return false;
			if (!ev.inProgress) return true;

			return Math.round(ev.width) === Math.round(task.$w);
		});

		// A move commits both dates; a resize commits only one of them
		const unsubscribeUpdateIntercept = ganttApi.intercept(
			"update-task",
			(ev) => {
				if (ev.eventSource) return true;
				if (!ganttApi.getTask(ev.id)?.isDraggable) return false;

				return Boolean(ev.task?.start && ev.task?.end);
			},
		);

		cleanupFunctions.push(
			unsubscribeTaskSelect,
			unsubscribeEditorIntercept,
			unsubscribeDragIntercept,
			unsubscribeUpdateIntercept,
			unsubscribeSortIntercept,
			unsubscribeAddIntercept,
			unsubscribeDeleteIntercept,
			unsubscribeAddLinkIntercept,
			unsubscribeDeleteLinkIntercept,
		);

		return () => {
			cleanupFunctions.forEach((cleanup) => {
				if (cleanup && typeof cleanup === "function") {
					cleanup();
				}
			});
		};
	}, [ganttApi, onTaskSelect]);
};

export default useGanttEventHandlers;
